/**
 * useSystemSettings.js
 *
 * Fetches the global system settings (currently just `signupEnabled`)
 * via the public getSystemSettings Cloud Function. The login and
 * register pages call this *before* auth so they know whether to
 * render the "create an account" link / form.
 *
 * Module-level cache: once fetched, the same promise is reused for
 * the rest of the page lifetime. There's no point hitting the function
 * multiple times for a setting that almost never changes mid-session,
 * and we want the login page to render instantly on subsequent mounts.
 */
import { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";

let cachedPromise = null;
// Defaults preserve original behaviour: both flags default to "on"
// so a system that hasn't written settings yet renders normally.
const FALLBACK = { signupEnabled: true, plansTabEnabled: true };

function fetchSettings() {
  // Cache the in-flight promise so concurrent callers get one request,
  // and subsequent callers get the resolved value instantly.
  if (cachedPromise) return cachedPromise;
  cachedPromise = (async () => {
    try {
      const fn = httpsCallable(functions, "getSystemSettings");
      const res = await fn({});
      return res.data?.settings || FALLBACK;
    } catch (err) {
      // Never block the UI on this — fall back to "open" defaults.
      // The backend itself enforces signup gating, so a fallback that
      // shows the link is safe (the function call would be rejected).
      // eslint-disable-next-line no-console
      console.warn("getSystemSettings failed, using fallback:", err?.message);
      return FALLBACK;
    }
  })();
  return cachedPromise;
}

/** Force a refetch on next access — used after admin toggles a flag. */
export function invalidateSystemSettings() {
  cachedPromise = null;
}

export function useSystemSettings() {
  const [settings, setSettings] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    let alive = true;
    fetchSettings().then(s => {
      if (!alive) return;
      setSettings(s);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  return { settings: settings || FALLBACK, loading };
}